import { BLEND_MODES, Container, Sprite, Texture } from "pixi.js"
import { stageViewport } from "../../../../canvas_layout"
import { choreoMs, realMs } from "./intro_pace"
import { BackdropEllipse, LOADING_BACKDROP_STOPS } from "./loading_handoff"
import { PixelBuffer } from "./pixel_raster"
import { PixelLayer } from "./pixel_layer"
import { LIVE_BOX, PX, ShipSprites, buildRocks, paintLive } from "./pixel_shipwreck_art"
import { SwellRow, buildSwell } from "./pixel_storm_art"
import { Bounds, REST, SCENE, SHIP_ON_RIGHT, easeInOutCubic, prog, shakeAt } from "./shipwreck_geometry"
import { StormNight } from "./storm_night"

/**
 * The login screen's backdrop as pixel art: the same shipwreck as the line-art scene
 * (shipwreck_scene.ts), on the same timeline, drawn the way the game's world is drawn -- in a gale
 * blowing onshore, which is what puts her on the rocks.
 *
 * Every piece is painted on a grid of art pixels PX logical pixels across and shown with
 * nearest-neighbour scaling, and everything that moves -- the shake included -- moves a whole art
 * pixel at a time, so nothing ever lands between two pixels:
 *  - The storm (sky, scud, lightning, sea, the far shore, the rain) is StormNight, shared with
 *    character creation.
 *  - The ship, the rocks she strikes, the swell washing over them, white water and spray are a small
 *    buffer repainted every frame, between the storm's two halves. The ship itself is only
 *    re-rasterized when her pose changes by something a pixel can show (ShipSprites). The lantern's
 *    light is a buffer of its own, added over everything with an ADD blend.
 */

/** Below this the warm loading backdrop has faded and is freed. */
const BACKDROP_DENSITY = 0.5

export interface PixelShipwreckSceneOptions {
    /** The loading screen's backdrop, when the scene takes over from it: drawn here, then faded. */
    backdrop?: BackdropEllipse
    /** Start with everything drawn and the ship already aground (prefers-reduced-motion). */
    atRest?: boolean
    /** No lightning flashes and no shake (prefers-reduced-motion). */
    calm?: boolean
}

export class PixelShipwreckScene extends Container {
    /** Everything but the loading backdrop: this is what shakes when the ship strikes. */
    private readonly world = new Container()
    private readonly storm: StormNight
    private readonly liveLayer = new PixelLayer()
    private readonly lightLayer = new PixelLayer()
    private readonly backdrop?: BackdropEllipse
    private readonly calm: boolean
    private backdropSprite?: Sprite

    private bounds: Bounds = { left: 0, right: 800, top: 0, bottom: 460 }
    private swell: SwellRow[] = []
    private readonly rocks = buildRocks()
    private readonly liveBuf = new PixelBuffer(LIVE_BOX.w, LIVE_BOX.h, LIVE_BOX.x0, LIVE_BOX.y0)
    private readonly lightBuf = new PixelBuffer(LIVE_BOX.w, LIVE_BOX.h, LIVE_BOX.x0, LIVE_BOX.y0)
    private readonly ships = new ShipSprites()

    private readonly startedAt: number
    private frame?: number
    private disposed = false

    constructor({ backdrop, atRest = false, calm = false }: PixelShipwreckSceneOptions = {}) {
        super()
        this.backdrop = backdrop
        this.calm = calm
        this.startedAt = Date.now() - (atRest ? realMs(REST) : 0)
        this.storm = new StormNight(!calm)
        this.lightLayer.sprite.blendMode = BLEND_MODES.ADD

        this.world.addChild(this.storm.back, this.liveLayer.sprite, this.lightLayer.sprite, this.storm.front)
        this.addChild(this.world)

        this.resize()
        this.tick()
    }

    /** Fit the scene to the visible stage: wider and taller than 800 x 460 on an expanded phone. */
    resize = () => {
        if (this.disposed) {
            return
        }
        const v = stageViewport()
        this.bounds = { left: v.left, right: v.right, top: v.top, bottom: v.bottom }
        this.storm.build(this.bounds)
        this.swell = buildSwell(this.bounds)
        this.liveLayer.place(LIVE_BOX.x0, LIVE_BOX.y0, LIVE_BOX.w, LIVE_BOX.h)
        this.lightLayer.place(LIVE_BOX.x0, LIVE_BOX.y0, LIVE_BOX.w, LIVE_BOX.h)
        this.bakeBackdrop()
        this.paint(this.now())
    }

    dispose = () => {
        this.disposed = true
        if (this.frame !== undefined && typeof cancelAnimationFrame === "function") {
            cancelAnimationFrame(this.frame)
        }
        this.frame = undefined
        this.dropBackdrop()
        this.storm.dispose()
        this.liveLayer.dispose()
        this.lightLayer.dispose()
        this.removeChildren()
        this.world.destroy({ children: true })
    }

    private now = () => choreoMs(Date.now() - this.startedAt)

    private tick = () => {
        if (this.disposed) {
            return
        }
        this.paint(this.now())
        if (typeof requestAnimationFrame === "function") {
            this.frame = requestAnimationFrame(this.tick)
        }
    }

    /**
     * The loading screen's own backdrop, repainted in pixi under the HTML one so the HTML layer can
     * drop away without the screen changing. It then fades to show the night behind it.
     */
    private bakeBackdrop = () => {
        const e = this.backdrop
        if (!e || this.now() >= SCENE.backdrop[1]) {
            this.dropBackdrop()
            return
        }
        const b = this.bounds
        try {
            const canvas = document.createElement("canvas")
            canvas.width = Math.max(1, Math.ceil((b.right - b.left) * BACKDROP_DENSITY))
            canvas.height = Math.max(1, Math.ceil((b.bottom - b.top) * BACKDROP_DENSITY))
            const g = canvas.getContext("2d")
            if (!g) {
                return
            }
            g.fillStyle = LOADING_BACKDROP_STOPS[LOADING_BACKDROP_STOPS.length - 1][1]
            g.fillRect(0, 0, canvas.width, canvas.height)
            g.setTransform(BACKDROP_DENSITY, 0, 0, BACKDROP_DENSITY, -b.left * BACKDROP_DENSITY, -b.top * BACKDROP_DENSITY)
            g.translate(e.cx, e.cy)
            g.scale(1, e.ry / e.rx)
            const gradient = g.createRadialGradient(0, 0, 0, 0, 0, e.rx)
            LOADING_BACKDROP_STOPS.forEach(([offset, color]) => gradient.addColorStop(offset, color))
            g.fillStyle = gradient
            g.fillRect(-e.rx, -e.rx, e.rx * 2, e.rx * 2)

            this.dropBackdrop()
            const sprite = new Sprite(Texture.from(canvas))
            sprite.position.set(b.left, b.top)
            sprite.scale.set(1 / BACKDROP_DENSITY)
            this.backdropSprite = sprite
            this.addChild(sprite)
        } catch (err) {
            // No canvas: no backdrop. The HTML one fades over the scene instead.
        }
    }

    private dropBackdrop = () => {
        const sprite = this.backdropSprite
        if (!sprite) {
            return
        }
        this.backdropSprite = undefined
        sprite.parent?.removeChild(sprite)
        sprite.destroy({ texture: true, baseTexture: true })
    }

    private paint = (t: number) => {
        if (this.backdropSprite) {
            const alpha = 1 - easeInOutCubic(prog(t, SCENE.backdrop[0], SCENE.backdrop[1]))
            if (alpha <= 0) {
                this.dropBackdrop()
            } else {
                this.backdropSprite.alpha = alpha
            }
        }

        this.storm.paint(t)
        this.paintLive(t)

        // The strike shakes the screen a whole art pixel at a time.
        const [sx, sy] = this.calm ? [0, 0] : shakeAt(t)
        this.world.position.set(Math.round((SHIP_ON_RIGHT ? -sx : sx) / PX) * PX, Math.round(sy / PX) * PX)
    }

    private paintLive = (t: number) => {
        const showing = t >= Math.min(SCENE.shipInk[0], SCENE.rocks[0])
        this.liveLayer.sprite.visible = showing
        this.lightLayer.sprite.visible = showing
        if (!showing) {
            return
        }
        paintLive(this.liveBuf, t, this.rocks, this.ships, { swell: this.swell, light: this.lightBuf, lightning: !this.calm })
        this.liveLayer.commit(this.liveBuf)
        this.lightLayer.commit(this.lightBuf)
    }
}
